/**
 * Header da janela de chat (avatar, tags, ações da conversa).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChatConversation } from '../../hooks/types';
import { ChatAvatar } from './ChatAvatar';
import { ActionButton } from './ActionButton';
import { TransferModal } from './TransferModal';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { useChatHeader } from '../../hooks/useChatHeader';
import { useChatFavorites } from '../../hooks/useChatFavorites';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useSyncAvatar } from '../../hooks/useSyncAvatar';
import { formatInstagramHandle, isUsableContactName } from '../../../../utils/phoneUtils';

interface ChatHeaderProps {
    conversation: ChatConversation;
    onTransfer?: (userId: string) => void;
    selectedTagIds?: string[];
    onCloseConversation?: () => void;
    onReopenConversation?: () => void;
    onToggleSearch?: () => void;
    isSearchOpen?: boolean;
    onBack?: () => void;
    isContactPanelOpen?: boolean;
    onToggleContactPanel?: () => void;
}

const iconBtnBase =
    'inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg transition-colors duration-150 ease-[cubic-bezier(.4,0,.2,1)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

const iconBtnActive = 'bg-primary-subtle text-primary';
const iconBtnIdle = 'text-muted hover:bg-primary-subtle hover:text-primary';
const iconBtnIdleMobile =
    'text-muted hover:bg-primary-subtle hover:text-primary active:bg-primary-subtle active:text-primary';

const toggleBtnClass = (active: boolean, on: string, off: string) =>
    `${iconBtnBase} ${active ? on : off}`;

export const ChatHeader: React.FC<ChatHeaderProps> = ({
    conversation,
    onTransfer,
    selectedTagIds = [],
    onCloseConversation,
    onReopenConversation,
    onToggleSearch,
    isSearchOpen = false,
    onBack,
    isContactPanelOpen,
    onToggleContactPanel,
}) => {
    const {
        isTransferModalOpen, setIsTransferModalOpen, selectedTags, isClosed, crmUsers,
        handleTransfer, handleCloseConversation, handleReopenConversation,
    } = useChatHeader({
        conversation, selectedTagIds, onTransfer, onCloseConversation, onReopenConversation,
    });

    const { isFavorite, toggleFavorite } = useChatFavorites();
    const isConversationFavorite = isFavorite(conversation.id);
    const isMember = useOrganization().currentOrganization?.role === 'member';
    const { syncAvatar, isSyncing } = useSyncAvatar();

    const [localAvatar, setLocalAvatar] = useState(conversation.avatar);
    useEffect(() => {
        setLocalAvatar(conversation.avatar);
    }, [conversation.avatar]);

    const isInstagram = conversation.platform === 'instagram';
    const displayName = useMemo(() => {
        const raw = conversation.name?.trim() || '';
        if (!isInstagram || isUsableContactName(raw)) return conversation.name;
        return formatInstagramHandle(conversation.metaAttribution?.instagramUsername) || conversation.name;
    }, [conversation.name, conversation.metaAttribution?.instagramUsername, isInstagram]);

    const handleSyncAvatar = async () => {
        if (isInstagram || !conversation.contactId) return;
        const url = await syncAvatar(conversation.contactId, conversation.instanceName);
        if (url) setLocalAvatar(url);
    };

    const favoriteLabel = isConversationFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
    const archiveLabel = isClosed ? 'Reabrir conversa' : 'Encerrar conversa';

    return (
        <>
            <div
                className="flex min-h-14 w-full shrink-0 items-center justify-between gap-s2 px-s4 pt-[env(safe-area-inset-top)] md:min-h-16 md:gap-s3 md:px-s6 md:pt-0"
                aria-label="Cabeçalho da conversa"
            >
                <div className="flex min-w-0 flex-1 items-center gap-s2 md:gap-s3">
                    {onBack && (
                        <button
                            type="button"
                            onClick={onBack}
                            className={`-ml-s1 shrink-0 ${iconBtnBase} text-muted hover:bg-surface-subtle md:hidden`}
                            aria-label="Voltar para conversas"
                        >
                            <AppIcon name="arrow-left" className="size-6 stroke-[1.5]" aria-hidden="true" />
                        </button>
                    )}
                    <ChatAvatar
                        name={displayName}
                        avatar={localAvatar}
                        size="lg"
                        onClick={isInstagram ? undefined : handleSyncAvatar}
                        isSyncing={isInstagram ? false : isSyncing[conversation.contactId || '']}
                        customBadgeIcon={isInstagram ? 'instagram' : 'whatsapp'}
                    />
                    <div className="min-w-0 flex-1">
                        <h2 className="chat-contact-name truncate">{displayName}</h2>
                        {selectedTags.length > 0 && (
                            <ul className="mt-s1 hidden flex-wrap gap-s1 md:flex" aria-label="Tags do contato">
                                {selectedTags.map((tag) => (
                                    <li key={tag.id}>
                                        <span
                                            className="inline-flex items-center rounded-xs px-s2 py-0.5 text-xs font-medium uppercase tracking-wide"
                                            style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
                                        >
                                            {tag.name}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {onToggleContactPanel && (
                        <button
                            type="button"
                            onClick={onToggleContactPanel}
                            className={`shrink-0 xl:hidden ${toggleBtnClass(
                                Boolean(isContactPanelOpen),
                                iconBtnActive,
                                iconBtnIdleMobile,
                            )}`}
                            title="Dados do contato"
                            aria-label="Dados do contato"
                            aria-expanded={Boolean(isContactPanelOpen)}
                            aria-controls={isContactPanelOpen ? 'chat-contact-panel' : undefined}
                        >
                            <AppIcon name="info" className="size-5 stroke-[1.5]" aria-hidden="true" />
                        </button>
                    )}
                </div>

                <nav
                    className="flex shrink-0 items-center gap-s1 sm:gap-s2"
                    aria-label="Ações da conversa"
                >
                    {isMember && (
                        <button
                            type="button"
                            onClick={() => void toggleFavorite(conversation.id)}
                            className={toggleBtnClass(
                                isConversationFavorite,
                                'bg-warning-bg text-warning-text',
                                'text-muted hover:bg-warning-bg hover:text-warning-text',
                            )}
                            title={favoriteLabel}
                            aria-label={favoriteLabel}
                        >
                            <AppIcon
                                name={isConversationFavorite ? 'star-filled' : 'star'}
                                className="size-5"
                                aria-hidden="true"
                            />
                        </button>
                    )}

                    <ActionButton
                        title="Transferir conversa para outro usuário"
                        colorClass={iconBtnIdle}
                        iconName="arrow-right"
                        onClick={() => setIsTransferModalOpen(true)}
                        ariaLabel="Transferir conversa"
                    />

                    <button
                        type="button"
                        onClick={() => onToggleSearch?.()}
                        className={toggleBtnClass(isSearchOpen, iconBtnActive, iconBtnIdle)}
                        title="Pesquisar na conversa"
                        aria-label="Pesquisar na conversa"
                        aria-pressed={isSearchOpen}
                    >
                        <AppIcon name="search" className="size-5 stroke-[1.5]" aria-hidden="true" />
                    </button>

                    <button
                        type="button"
                        onClick={isClosed ? handleReopenConversation : handleCloseConversation}
                        className={toggleBtnClass(
                            isClosed,
                            'text-muted hover:bg-surface-subtle hover:text-primary',
                            'text-muted hover:bg-danger-bg hover:text-danger-text',
                        )}
                        title={archiveLabel}
                        aria-label={archiveLabel}
                    >
                        <AppIcon
                            name={isClosed ? 'archive-restore' : 'archive'}
                            className="size-5 stroke-[1.5] opacity-90"
                            aria-hidden="true"
                        />
                    </button>

                    {onToggleContactPanel && !isContactPanelOpen && (
                        <button
                            type="button"
                            onClick={onToggleContactPanel}
                            className={`hidden xl:inline-flex ${iconBtnBase} ${iconBtnIdle}`}
                            title="Mostrar detalhes do contato"
                            aria-label="Mostrar detalhes do contato"
                        >
                            <AppIcon name="info" className="size-5 stroke-[1.5]" aria-hidden="true" />
                        </button>
                    )}
                </nav>
            </div>

            <TransferModal
                isOpen={isTransferModalOpen}
                users={crmUsers}
                onTransfer={handleTransfer}
                onClose={() => setIsTransferModalOpen(false)}
            />
        </>
    );
};
